import { Link } from 'react-router-dom';
import { Mail, MapPin } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import peptLogo from '../assets/pept-logo.png';

// PEP-T Logo Component using original image (footer version)
const PeptLogoFooter = ({ className = "" }) => (
  <img 
    src={peptLogo} 
    alt="PEP-T.COM" 
    className={`h-10 w-auto ${className}`}
    style={{ maxHeight: "40px" }}
  />
);

export const Footer = () => {
  const { t, language } = useLanguage();

  const quickLinks = [
    { path: '/', label: t('nav.home') },
    { path: '/catalog', label: t('nav.catalog') },
    { path: '/coa', label: t('nav.coa') },
    { path: '/contact', label: t('nav.contact') },
  ];

  return (
    <footer className="bg-neutral-900 text-white" data-testid="footer">
      {/* Export Notice Banner */}
      <div className="bg-red-600 py-3">
        <div className="container-custom text-center">
          <p className="text-sm font-medium text-white">
            {t('footer.exportNotice')}
          </p>
        </div>
      </div>

      <div className="container-custom py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="lg:col-span-2">
            <Link to="/" className="inline-block mb-4" data-testid="footer-logo">
              <PeptLogoFooter />
            </Link>
            <p className="text-neutral-400 text-sm max-w-md mb-6">
              {t('footer.description')}
            </p>
            <div className="space-y-2">
              <div className="flex items-center gap-3 text-sm text-neutral-400">
                <Mail className="w-4 h-4" />
                <a
                  href="mailto:support@pep-t.com"
                  className="hover:text-white transition-colors"
                  data-testid="footer-email"
                >
                  support@pep-t.com
                </a>
              </div>
              <div className="flex items-center gap-3 text-sm text-neutral-400">
                <MapPin className="w-4 h-4" />
                <span>Boca Raton, Florida, USA</span>
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-sm font-semibold uppercase tracking-wide mb-4">
              {t('footer.quickLinks')}
            </h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-sm text-neutral-400 hover:text-white transition-colors"
                    data-testid={`footer-link-${link.path.replace('/', '') || 'home'}`}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h4 className="text-sm font-semibold uppercase tracking-wide mb-4">
              {t('footer.legal')}
            </h4>
            <ul className="space-y-3">
              <li>
                <Link
                  to="/terms"
                  className="text-sm text-neutral-400 hover:text-white transition-colors"
                  data-testid="footer-terms"
                >
                  {t('footer.terms')}
                </Link>
              </li>
              <li>
                <Link
                  to="/privacy"
                  className="text-sm text-neutral-400 hover:text-white transition-colors"
                  data-testid="footer-privacy"
                >
                  {t('footer.privacy')}
                </Link>
              </li>
              <li>
                <Link
                  to="/disclaimer"
                  className="text-sm text-neutral-400 hover:text-white transition-colors"
                  data-testid="footer-disclaimer"
                >
                  {t('footer.disclaimer')}
                </Link>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-neutral-800">
          {/* Legal Disclaimer - TSCA Reference */}
          <div className="mb-6 p-4 bg-neutral-800/50 rounded-sm">
            <p className="text-xs text-neutral-400 leading-relaxed">
              {language === 'pt' 
                ? 'AVISO LEGAL: Os produtos vendidos pela PEP-T.COM são classificados como produtos químicos de pesquisa sob a Lei de Controle de Substâncias Tóxicas (TSCA). De acordo com a Seção 5(h)(3) da TSCA (15 U.S.C. § 2604) e 40 CFR 720.36, estes produtos são destinados exclusivamente para pesquisa laboratorial in-vitro e NÃO são medicamentos, dispositivos médicos, suplementos alimentares ou cosméticos para uso humano ou animal. Todos os produtos são vendidos apenas para exportação.'
                : 'LEGAL NOTICE: Products sold by PEP-T.COM are classified as research chemicals under the Toxic Substances Control Act (TSCA). Pursuant to Section 5(h)(3) of TSCA (15 U.S.C. § 2604) and 40 CFR 720.36, these products are intended solely for in-vitro laboratory research and are NOT drugs, medical devices, dietary supplements, or cosmetics for human or animal use. All products are sold for export only.'}
            </p>
          </div>
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-xs text-neutral-500">
              {t('footer.copyright')}
            </p>
            <p className="text-xs text-neutral-500">
              {t('footer.researchOnly')}
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};
